/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { resolveWaitMs } from './countdown';

export interface RateLimitState {
    /** True while a rate-limit notice should be shown to the user */
    isRateLimited: boolean;
    /**
     * Milliseconds the server asked us to wait, parsed from the `retry-after`
     * response header. Null when the header was absent or un-parseable.
     *
     * The server's own value, not the wait the notice acts on: `./countdown` falls
     * back to a one-minute default for anything outside thirty seconds to five
     * minutes. A service reacting to this itself should decide what its own UI does
     * about a value the notice would not count down.
     */
    retryAfterMs: number | null;
}

const initialState: RateLimitState = {
    isRateLimited: false,
    retryAfterMs: null,
};

let state: RateLimitState = initialState;
const listeners = new Set<() => void>();

/**
 * When the wait currently being shown started, so the suppression window can end
 * where that wait ends rather than a whole wait after whenever the visitor got
 * round to acknowledging the notice. Null while no notice is up.
 */
let waitStartedAt: number | null = null;

/**
 * Timestamp until which 429s raise nothing, set when the notice is acknowledged.
 * Null when nothing is being suppressed.
 */
let suppressedUntil: number | null = null;

function setState(nextState: RateLimitState) {
    state = nextState;
    listeners.forEach((listener) => listener());
}

/** Takes the notice down. Any suppression window survives, which is the point. */
function clearNotice() {
    waitStartedAt = null;

    if (state === initialState) {
        return;
    }

    setState(initialState);
}

/**
 * True while the visitor has acknowledged a notice and the wait they were told to
 * sit out has not elapsed.
 */
function isSuppressed() {
    if (suppressedUntil === null) {
        return false;
    }

    if (Date.now() < suppressedUntil) {
        return true;
    }

    suppressedUntil = null;

    return false;
}

/**
 * Module-level store for global rate-limit (HTTP 429) state.
 *
 * This lives outside React so an axios interceptor — or a service detecting a 429
 * some other way — can report one without needing a component, a hook, or a
 * reference to any service's redux store. React reads it through `useRateLimit`,
 * which subscribes with `useSyncExternalStore`.
 *
 * **Internal to this module.** Nothing outside `shared-ui/rate-limit` should
 * import it: services report a rate limit with `reportRateLimit` and read state
 * with `useRateLimit`. Tests reset it through the `testing` entry point.
 *
 * `getSnapshot` returns a stable reference between changes, which
 * `useSyncExternalStore` requires to avoid re-render loops.
 */
export const rateLimitStore = {
    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    },

    getSnapshot(): RateLimitState {
        return state;
    },

    /**
     * Flags the app as rate limited. A burst of parallel requests can each come
     * back 429, but the user should only see one notice, so this is a no-op while
     * already rate limited.
     *
     * The one thing a repeat trigger can still do is supply a `retry-after`
     * window we do not have yet. Most services on this platform do not send the
     * header, so a header-less 429 landing first would otherwise discard the real
     * window from a service that does send it milliseconds later. A window
     * already recorded is never overwritten.
     *
     * It is also a no-op while the visitor is sitting out a wait they already
     * acknowledged, so the 429s a still-broken page keeps producing do not raise
     * the notice again and again.
     */
    trigger(retryAfterMs: number | null = null) {
        if (isSuppressed()) {
            return;
        }

        if (state.isRateLimited) {
            if (state.retryAfterMs === null && retryAfterMs !== null) {
                // The notice restarts its countdown from the backfilled wait, so
                // the wait now starts here and the suppression window derived from
                // it ends where that countdown does
                waitStartedAt = Date.now();
                setState({ isRateLimited: true, retryAfterMs });
            }

            return;
        }

        waitStartedAt = Date.now();
        setState({ isRateLimited: true, retryAfterMs });
    },

    /**
     * Dismisses the notice, e.g. when the user acknowledges it, and swallows
     * further 429s for the rest of the wait the visitor was just told about — the
     * page behind the notice is still broken and still firing requests. See the
     * README's "Suppression after acknowledgement" for why the window ends where
     * the wait ends rather than a whole wait later.
     */
    clear() {
        if (waitStartedAt !== null) {
            suppressedUntil = waitStartedAt + resolveWaitMs(state.retryAfterMs);
        }

        clearNotice();
    },

    /**
     * Drops all state, including any suppression window. For tests only, reached
     * through the module's `testing` entry point — this store is module-level, so
     * state left behind by one test is still there for the next one, and a leaked
     * window would silently swallow the next test's notice.
     */
    reset() {
        suppressedUntil = null;
        clearNotice();
    },
};
